import { Component, OnInit, OnDestroy } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {forkJoin, Subscription} from 'rxjs';
import { AQH_EVE_0 } from "../../../../../../@shared/models/input.module";
import {EventResponse, CoOrganiserAccessLevel, EventClassResponse, EventServiceResponse, TrainingPackageResponse, EventAccessResponse} from '../../../../../../@shared/models/response.module';
import {EventsService} from '../../../../../../@shared/services/events-service';
import {TrainingService} from '../../../../../../@shared/services/training.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {UpdateEventImage} from './components/update-event-image/update-event-image';
import {StorageService} from '../../../../../../@core/services/storage.service';
import {CancelEventModal} from './components/cancel-event-modal/cancel-event-modal';
import {CompanyService} from '../../../../../../@shared/services/company.service';

type EventCategory = 'competition' | 'training' | 'social';

@Component({
  selector: 'app-manage-event',
  standalone: false,
  templateUrl: './manage-event.html',
  styleUrl: './manage-event.css'
})
export class ManageEvent implements OnInit, OnDestroy {
  eventId: string
  event: EventResponse
  selectedTab: string = 'overview'
  publishLoading: boolean = false

  // Co-organiser access tracking
  accessLevel: CoOrganiserAccessLevel | 'owner' | null = null;

  // Payment validation data
  eventClasses: EventClassResponse[] = [];
  eventServices: EventServiceResponse[] = [];
  trainingPackages: TrainingPackageResponse[] = [];
  paymentValidationLoading: boolean = false;

  // Query params for deep linking
  selectedClassId: number | null = null;
  private queryParamsSub: Subscription;

  get category(): EventCategory {
    return this.event?.category || 'competition';
  }

  get showClasses(): boolean {
    return this.category === 'competition';
  }

  get showCapacity(): boolean {
    return this.category === 'training';
  }

  get showPackages(): boolean {
    return this.category === 'training';
  }

  get showSchedule(): boolean {
    return this.category === 'competition';
  }

  get showTrainingSchedule(): boolean {
    return this.category === 'training';
  }

  // Permission getters based on access level
  get isEventOwner(): boolean {
    return this.accessLevel === 'owner';
  }

  get canEdit(): boolean {
    return this.accessLevel === 'owner' || this.accessLevel === 'full' || this.accessLevel === 'limited';
  }

  get canManageClasses(): boolean {
    return this.accessLevel === 'owner' || this.accessLevel === 'full';
  }

  get canPublish(): boolean {
    return this.accessLevel === 'owner' || this.accessLevel === 'full';
  }

  get canManageCoOrganisers(): boolean {
    return this.accessLevel === 'owner';
  }

  get canDeleteEvent(): boolean {
    return this.accessLevel === 'owner';
  }

  get canCancelEvent(): boolean {
    return this.accessLevel === 'owner' && this.event?.status !== 'cancelled';
  }

  get isCancelled(): boolean {
    return this.event?.status === 'cancelled';
  }

  // Payment validation getters
  get hasPricedClasses(): boolean {
    return this.eventClasses?.some(c => c.price && c.price > 0) || false;
  }

  get hasPricedServices(): boolean {
    return this.eventServices?.some(s => s.price && s.price > 0) || false;
  }

  get hasPricedPackages(): boolean {
    return this.trainingPackages?.some(p => p.price_cents && p.price_cents > 0) || false;
  }

  get hasPricedItems(): boolean {
    return this.hasPricedClasses || this.hasPricedServices || this.hasPricedPackages;
  }

  get hasPaymentSetup(): boolean {
    const company = this.event?.company;
    if (!company) return false;
    return company.stripe_account_status === 'active' || company.custom_payment_enabled === true;
  }

  get hasPaymentIssue(): boolean {
    if (!this.hasPricedItems) return false;
    if (!this.hasPaymentSetup) return true;
    if (!this.event?.payment_enabled) return true;
    return false;
  }

  get canPublishEvent(): boolean {
    if (!this.event) return false;
    if (this.hasPaymentIssue) return false;
    return true;
  }

  get publishBlockedMessage(): string {
    if (!this.hasPricedItems) return '';

    if (!this.hasPaymentSetup) {
      return 'manageEvent.publishBlocked.noPaymentSetup';
    }
    if (!this.event?.payment_enabled) {
      return 'manageEvent.publishBlocked.paymentDisabled';
    }
    return '';
  }

  get pricedItemsList(): string[] {
    const items: string[] = [];
    if (this.hasPricedClasses) items.push('classes');
    if (this.hasPricedServices) items.push('services');
    if (this.hasPricedPackages) items.push('packages');
    return items;
  }

  constructor(private route: ActivatedRoute,
              private eventService: EventsService,
              private trainingService: TrainingService,
              private companyService: CompanyService,
              private modalService: NgbModal,
              private storageService: StorageService) {
  }

  ngOnInit() {
    this.eventId = String(this.route.snapshot.paramMap.get('id'));
    this.getEvent();

    // Subscribe to query params for deep linking (e.g., from schedule page results button)
    this.queryParamsSub = this.route.queryParams.subscribe(params => {
      if (params['tab']) {
        this.selectedTab = params['tab'];
      }
      if (params['classId']) {
        this.selectedClassId = Number(params['classId']);
      }
    });
  }

  ngOnDestroy() {
    if (this.queryParamsSub) {
      this.queryParamsSub.unsubscribe();
    }
  }

  getEvent() {
    let params: AQH_EVE_0 = {
      event_id: Number(this.eventId)
    }
    this.eventService.getEvents(params).subscribe((res) => {
      this.event = res.data[0];
      this.determineAccessLevel();
      this.loadPaymentValidationData();

      // Fallback: load company separately if not included in event response
      if (!this.event.company && this.event.company_id) {
        this.companyService.getCompanyById({ company_id: this.event.company_id }).subscribe({
          next: (company) => {
            if (company) {
              this.event.company = company;
            }
          }
        });
      }
    })
  }

  loadPaymentValidationData() {
    if (!this.event?.event_id) return;

    this.paymentValidationLoading = true;
    const eventId = this.event.event_id;

    // Load classes
    this.eventService.getEventClasses({ event_id: eventId }).subscribe({
      next: (classes) => {
        this.eventClasses = classes || [];
      }
    });

    // Load services
    this.eventService.getEventServices({ event_id: eventId }).subscribe({
      next: (services) => {
        this.eventServices = services || [];
      }
    });

    // Load packages (for training events)
    this.trainingService.getPackages({ event_id: eventId }).subscribe({
      next: (packages) => {
        this.trainingPackages = packages || [];
        this.paymentValidationLoading = false;
      },
      error: () => {
        this.paymentValidationLoading = false;
      }
    });
  }

  private determineAccessLevel() {
    if (!this.event) {
      this.accessLevel = null;
      return;
    }

    const currentCompanyId = this.storageService.getSelectedCompanyId();

    // Check if current company is the event owner
    if (this.event.company_id === currentCompanyId) {
      this.accessLevel = 'owner';
      return;
    }

    // Check if current company is a co-organiser
    // The co_organiser_access_level should be included in the event response when
    // the current company is a co-organiser
    if ((this.event as any).co_organiser_access_level) {
      this.accessLevel = (this.event as any).co_organiser_access_level;
      return;
    }

    // Fallback: check direct event membership via AQH-EVE-185
    this.eventService.getEventAccess({ event_id: this.event.event_id }).subscribe({
      next: (res: EventAccessResponse) => {
        if (res.access_level) {
          this.accessLevel = res.access_level;
        } else {
          this.accessLevel = null;
        }
      },
      error: () => {
        this.accessLevel = null;
      }
    });
  }

  openEditImageModal(imageType: 'header' | 'logo') {
    let modalRef = this.modalService.open(UpdateEventImage, {centered: true, size: 'md'});
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.imageType = imageType;
    modalRef.result.then((result) => {
      if (result) {
        this.getEvent();
      }
    }).catch(() => {});
  }

  publishEvent() {
    this.publishLoading = true;

    // First reload validation data to ensure we have the latest
    const eventId = this.event.event_id;
    forkJoin({
      classes: this.eventService.getEventClasses({ event_id: eventId }),
      services: this.eventService.getEventServices({ event_id: eventId }),
      packages: this.trainingService.getPackages({ event_id: eventId })
    }).subscribe({
      next: (results) => {
        this.eventClasses = results.classes || [];
        this.eventServices = results.services || [];
        this.trainingPackages = results.packages || [];

        // Check if we can publish after loading fresh data
        if (!this.canPublishEvent) {
          this.publishLoading = false;
          return;
        }

        // Proceed with publishing
        this.eventService.publishEvent(this.event.event_id).subscribe({
          next: (updatedEvent) => {
            this.event = updatedEvent;
            this.publishLoading = false;
          },
          error: () => {
            this.publishLoading = false;
          }
        });
      },
      error: () => {
        this.publishLoading = false;
      }
    });
  }

  unpublishEvent() {
    this.publishLoading = true;
    this.eventService.unpublishEvent(this.event.event_id).subscribe({
      next: (updatedEvent) => {
        this.event = updatedEvent;
        this.publishLoading = false;
      },
      error: () => {
        this.publishLoading = false;
      }
    });
  }

  openCancelEventModal() {
    const modalRef = this.modalService.open(CancelEventModal, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.event = this.event;
    modalRef.result.then((result) => {
      if (result?.cancelled) {
        this.getEvent();
      }
    }).catch(() => {});
  }

}
